import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { 
    Autocomplete, 
    TextField, 
    Collapse, 
    Box, 
    Typography, 
    IconButton 
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";

const CustomSkillsDropdown = forwardRef(({ 
    objectives, 
    numberOfSkills, 
    isDisabled, 
    initialSkills = null, 
    onChange
}, ref) => {
    const [customSkills, setCustomSkills] = useState([]);
    const [isExpanded, setIsExpanded] = useState(true);
    
    // Filter objectives to only include skills
    const skillObjectives = objectives.filter(objective => objective.type === 'skill');

    // Initialize custom skills array when numberOfSkills changes
    useEffect(() => {
        setCustomSkills(prevSkills => {
            const newSkills = Array.from({ length: numberOfSkills }, (_, index) => {
                // First check if we have an existing skill at this index
                if (index < prevSkills.length && prevSkills[index]) {
                    return prevSkills[index];
                }
                // Then check if we have initial skills
                if (initialSkills) {
                    const splitList = initialSkills.split('-');
                    if(splitList[index]) {
                        return skillObjectives.find(skill => skill.id === splitList[index]) || null;
                    }
                }
                return null;
            });
            return newSkills;
        });
    }, [numberOfSkills, skillObjectives, initialSkills]);

    // Handle skill selection change
    const handleCustomSkillChange = (event, newSkill, index) => {
        const updatedSkills = [...customSkills];
        updatedSkills[index] = newSkill;
        setCustomSkills(updatedSkills);
        onChange(updatedSkills.every(skill => skill !== null));
    };

    // Calculate total difficulty from selected skills
    const getTotalDifficulty = () => {
    const total = customSkills.reduce((total, skill) => {
        return total + (skill ? parseFloat(skill.difficulty || 0) : 0);
    }, 0);
    return parseFloat(total.toFixed(1));
};

    // Get selected skill IDs
    const getSelectedSkillIds = () => {
        return customSkills.map(skill => skill ? skill.id : null).filter(id => id !== null).join('-');
    };

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        getSelectedSkills: () => customSkills,
        getTotalDifficulty: getTotalDifficulty,
        getSelectedSkillIds: getSelectedSkillIds,
        isAllSkillsSelected: () => customSkills.every(skill => skill !== null),
        numberSkillsSelected: () => customSkills.filter(skill => skill !== null).length,
        getSkillsNameList: () => customSkills.map(skill => skill ? skill.name : '').filter(name => name !== '').join(', ')
    }));

    return (
        <Box sx={{ 
            marginTop: '10px',
            border: '1px solid #d1d5db',
            borderRadius: '0.5rem',
            backgroundColor: isDisabled ? '#f3f4f6' : '#ffffff',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
        }}>
            {/* Header with expand/collapse toggle */}
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    padding: '1rem',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Typography variant="h6" sx={{ 
                        fontSize: '1.125rem', 
                        fontWeight: '500',
                        margin: 0
                    }}>
                        Skills
                    </Typography>
                    <Typography variant="body2" sx={{ 
                        fontSize: '0.875rem',
                        color: '#6b7280'
                    }}>
                        Total Difficulty: {getTotalDifficulty().toFixed(1)}
                    </Typography>
                </Box>
                <IconButton size="small" disabled={isDisabled}>
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
            </Box>

            {/* Collapsible dropdown list */}
            <Collapse in={isExpanded}>
                <Box sx={{ 
                    paddingLeft: '1rem',
                    paddingRight: '1rem',
                    paddingBottom: '1rem',
                    paddingTop: 0
                }}>
                    {Array.from({ length: numberOfSkills }).map((_, index) => (
                        <Autocomplete
                            key={index}
                            sx={{ 
                                marginTop: index === 0 ? '0.5rem' : '1rem'
                            }}
                            disabled={isDisabled}
                            options={skillObjectives}
                            getOptionLabel={option => option.name || ''}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={customSkills[index] || null}
                            onChange={(e, newSkill) => handleCustomSkillChange(e, newSkill, index)}
                            renderInput={params => (     
                                    <TextField
                                        {...params}
                                        label={`Search or select skill ${index + 1}`}
                                        variant="outlined"
                                        size="small"
                                        // sx={{
                                        //     '& .MuiOutlinedInput-root': {
                                        //         backgroundColor: isDisabled ? '#f3f4f6' : '#ffffff',
                                        //         '& fieldset': {
                                        //             borderColor: '#d1d5db',
                                        //         },
                                        //         '&:hover fieldset': {
                                        //             borderColor: '#9ca3af',
                                        //         },
                                        //         '&.Mui-focused fieldset': {
                                        //             borderColor: '#3b82f6',
                                        //         },
                                        //     },
                                        // }}
                                    />
                                )
                            }
                        />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
});

CustomSkillsDropdown.displayName = 'CustomSkillsDropdown';

export default CustomSkillsDropdown;